import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/pt-br";
import "dayjs/locale/es";
import USAFlag from "../../../assets/images/FLAG_USA.png";
import BrazilFlag from "../../../assets/images/FLAG_BRAZIL.png";
import { LANGUAGE_KEY, toDayjsLocale } from "../../../constants/i18n";
import { getCookieDomainSafe } from "../../../utils/authStorage";

interface LanguageOption {
  value: string;
  label: string;
  flag: string | null;
  emoji?: string;
}

const languageOptions: LanguageOption[] = [
  { value: "en", label: "English (US)", flag: USAFlag },
  { value: "pt", label: "Português (BR)", flag: BrazilFlag },
  { value: "es", label: "Español (ES)", flag: null, emoji: "🇪🇸" },
];

/**
 * Language selector — mirrors the ACT SI Console / Admin Portal. Changing the
 * language calls i18next (which re-renders every `t()` consumer), keeps dayjs's
 * locale in step (it drives all date formatting), and persists the choice in the
 * shared `language` cookie so it applies across ACT consoles and survives
 * sessions.
 */
function LanguageSelectorSettingsPage() {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const initialLang = Cookies.get(LANGUAGE_KEY) || languageOptions[0].value;
  const [selectedLang, setSelectedLang] = useState(initialLang);

  // Apply the cookie's language on mount, then track it thereafter. Keyed on
  // i18n.language so it covers both the initial sync and any later change.
  useEffect(() => {
    if (initialLang !== i18n.language) {
      i18n.changeLanguage(initialLang);
      return;
    }
    dayjs.locale(toDayjsLocale(i18n.language));
  }, [i18n, initialLang, i18n.language]);

  const handleChange = (newLang: string) => {
    setSelectedLang(newLang);
    i18n.changeLanguage(newLang);
    dayjs.locale(toDayjsLocale(newLang));
    Cookies.set(LANGUAGE_KEY, newLang, {
      domain: getCookieDomainSafe(),
      secure: false,
      sameSite: "Lax",
    });
  };

  return (
    <Select
      value={selectedLang}
      onChange={(event) => handleChange(event.target.value)}
      displayEmpty
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: "18rem",
        borderRadius: "0.25rem",
        border: "0.1rem solid",
        height: "3rem",
        minHeight: "2.875rem",
        backgroundColor: theme.palette.mode === "dark" ? "#121212" : "#ececec",
        color: theme.palette.text.primary,
        "& .MuiSelect-select": { display: "flex", alignItems: "center" },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.divider,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.text.primary,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
          boxShadow: "none",
        },
      }}
    >
      {languageOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {option.flag ? (
              <img
                src={option.flag}
                alt={option.label}
                style={{ width: "1.3rem", height: "1.3rem", margin: "0 0.5rem 0 0" }}
              />
            ) : (
              <Typography sx={{ fontSize: "1.2rem", margin: "0 0.5rem 0 0", lineHeight: 1 }}>
                {option.emoji}
              </Typography>
            )}
            <Typography sx={{ fontSize: "1rem", color: theme.palette.text.primary }}>
              {option.label}
            </Typography>
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
}

export default LanguageSelectorSettingsPage;
